"use client"

import { useEffect, useState } from "react"
import API from "../services/api"
import "./Dashboard.css"

// Translation system
const translations = {
  fr: {
    back: "Retour",
    welcomeBooklet: "A propos de l'hôtel ",
    welcomeMessage: "Bienvenue à l'IBIS Tunis",
    loading: "Chargement...",
    noContentAvailable: "Aucun contenu disponible",
    checkBackLater: "Revenez plus tard pour consulter A propos de l'hôtel",
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Réseau Accor",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
    previous: "Précédent",
    next: "Suivant",
  },
  en: {
    back: "Back",
    welcomeBooklet: "About the hotel",
    welcomeMessage: "Welcome to IBIS Tunis",
    loading: "Loading...",
    noContentAvailable: "No content available",
    checkBackLater: "Check back later for information about the hotel",
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "accor network",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
    previous: "Previous",
    next: "Next",
  },
  ar: {
    back: "رجوع",
    welcomeBooklet: "حول الفندق",
    welcomeMessage: "مرحبا بكم في إيبيس تونس",
    loading: "جاري التحميل...",
    noContentAvailable: "لا يوجد محتوى متاح",
    checkBackLater: "عد لاحقًا للاطلاع على معلومات حول الفندق",
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "شبكة أكور",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، 3000 تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
    previous: "السابق",
    next: "التالي",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]

const LivretClient = () => {
  const [pageContent, setPageContent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [currentLanguage, setCurrentLanguage] = useState("fr")
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)

  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  const fetchPageContent = async () => {
    try {
      setIsLoading(true)
      console.log("[v0] Fetching Livret page content...")
      const res = await API.get("/page-contents/page/Livret")
      console.log("[v0] Page content received:", res.data)
      setPageContent(res.data)
    } catch (err) {
      console.error("[v0] Error fetching Livret page content:", err)
      setPageContent(null)
    } finally {
      setIsLoading(false)
    }
  }

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("IBIS-language", langCode)
    setShowLanguageDropdown(false)
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  const getRichDescriptionHtml = () => {
    const tr = pageContent?.translations || {}
    return (
      tr[currentLanguage]?.description ??
      tr.fr?.description ??
      pageContent?.description ??
      ""
    )
  }

  const renderContent = () => {
    if (!pageContent) {
      return (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>{t("noContentAvailable")}</h3>
          <p>{t("checkBackLater")}</p>
        </div>
      )
    }

    console.log("[v0] Rendering content, type:", pageContent.type)

    if (pageContent.type === "pdf" && pageContent.pdf) {
      console.log("[v0] Rendering PDF content")
      return (
        <div className="pdf-content-container">
          <iframe
            src={`${pageContent.pdf}#toolbar=0&navpanes=0&scrollbar=0`}
            className="pdf-iframe"
            title="A propos de l'hôtel PDF"
          />
        </div>
      )
    }

    const isRichMedia =
      pageContent.type === "default" ||
      pageContent.type === "video" ||
      pageContent.type === "mixed"

    if (isRichMedia) {
      const hasVideo = Boolean(pageContent.video)
      const hasImages = pageContent.images && pageContent.images.length > 0
      const descriptionHtml = getRichDescriptionHtml()

      return (
        <div className="page-content-default">
          {hasVideo && (
            <div className="video-content-container">
              <video
                src={pageContent.video}
                controls
                playsInline
                preload="metadata"
                className="content-video"
              />
            </div>
          )}

          {hasImages && (
            <div className="images-vertical-list">
              {pageContent.images.map((img, index) => (
                <div key={index} className="single-image-container">
                  <img
                    src={img || "/placeholder.svg"}
                    alt={`Livret content ${index + 1}`}
                    className="content-image"
                    onError={(e) => (e.target.src = "/placeholder.svg?height=400&width=800")}
                  />
                </div>
              ))}
            </div>
          )}

          {descriptionHtml && (
            <div className="content-description">
              <div
                className="page-content-html-light"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </div>
          )}
        </div>
      )
    }

    return null
  }

  useEffect(() => {
    fetchPageContent()
    window.scrollTo(0, 0)

    const savedLanguage = localStorage.getItem("IBIS-language")
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }
  }, [])

  return (
    <div className={`hotel-app4 ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <style jsx>{`
        * {
          box-sizing: border-box;
        }

        .hotel-app4 {
          max-width: 100vw;
          overflow-x: hidden;
        }

        .language-selector {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 20;
        }
        
        .language-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .language-toggle:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }
        
        .language-flag {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }
        
        .language-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          background: rgba(0, 0, 0, 0.9);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 150px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          margin-top: 5px;
        }
        
        .language-option {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
          gap: 8px;
          color: white;
          font-size: 14px;
        }
        
        .language-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .language-option.active {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .flag-small {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        .rtl {
          direction: rtl;
        }

        .rtl .language-selector {
          left: auto;
          right: 15px;
        }

        .rtl .language-dropdown {
          left: auto;
          right: 0;
          text-align: right;
        }

        .rtl .header-back-link {
          flex-direction: row;
        }

        .rtl .header-back-link svg {
          margin-right: 8px;
          margin-left: 0;
        }

        .page-content-default {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          width: 100%;
        }

        .single-image-container {
          margin-bottom: 30px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          width: 100%;
        }

        .content-image {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
          max-width: 100%;
        }

        .video-content-container {
          margin-bottom: 30px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          width: 100%;
          background: #0f0f0f;
        }

        .content-video {
          width: 100%;
          max-height: min(70vh, 560px);
          display: block;
          vertical-align: middle;
        }

        .image-slider {
          position: relative;
          margin-bottom: 30px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          width: 100%;
        }

        .slider-images {
          width: 100%;
          height: 400px;
          position: relative;
        }

        .slider-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .slider-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
        }

        .slider-btn:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }

        .prev-btn {
          left: 20px;
        }

        .next-btn {
          right: 20px;
        }

        .slider-dots {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 10;
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }

        .slider-dot:hover {
          background: rgba(255, 255, 255, 0.8);
        }

        .content-description {
          background: linear-gradient(135deg, rgba(106, 48, 147, 0.1) 0%, rgba(236, 64, 122, 0.1) 100%);
          padding: 30px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          width: 100%;
        }

        .content-description p {
          font-size: 16px;
          line-height: 1.8;
          color: #751313ff;
          margin: 0;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .pdf-content-container {
          width: 100%;
          height: calc(100vh - 150px);
          min-height: 500px;
          padding: 0;
          margin: 0;
        }

        .pdf-iframe {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
        }

        @media (max-width: 768px) {
          .page-content-default {
            padding: 15px;
          }

          .content-video {
            max-height: 50vh;
          }

          .slider-images {
            height: 250px;
          }

          .slider-btn {
            width: 36px;
            height: 36px;
          }

          .prev-btn {
            left: 10px;
          }

          .next-btn {
            right: 10px;
          }

          .slider-dots {
            bottom: 10px;
            gap: 6px;
          }

          .slider-dot {
            width: 8px;
            height: 8px;
          }

          .content-description {
            padding: 20px 15px;
          }

          .content-description p {
            font-size: 14px;
            line-height: 1.6;
          }

          .pdf-content-container {
            height: calc(100vh - 120px);
            min-height: 400px;
          }

          .language-selector {
            top: 10px;
            left: 10px;
          }

          .language-toggle {
            padding: 6px 10px;
            font-size: 12px;
          }

          .language-flag {
            width: 16px;
            height: 12px;
          }
        }

        @media (max-width: 480px) {
          .page-content-default {
            padding: 10px;
          }

          .slider-images {
            height: 200px;
          }

          .slider-btn {
            width: 32px;
            height: 32px;
          }

          .prev-btn {
            left: 5px;
          }

          .next-btn {
            right: 5px;
          }

          .content-description {
            padding: 15px 10px;
            border-radius: 8px;
          }

          .content-description p {
            font-size: 13px;
          }

          .single-image-container {
            margin-bottom: 20px;
            border-radius: 8px;
          }

          .image-slider {
            margin-bottom: 20px;
            border-radius: 8px;
          }
        }
      `}</style>

      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <header className="app-header">
        <button
          className="header-back-link"
          onClick={() => {
            window.location.href = "/home"
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("back")}
        </button>
        <div className="logo-container">
          <img src="/images/logo2.webp" alt="Ibis Logo" className="logo" />
        </div>
        <div></div>
      </header>

      <main className="app-main">
        <div className="welcome-banner">
          <h1>
            <span>{t("welcomeBooklet")}</span>
          </h1>
        </div>

        <div className="content-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t("loading")}</p>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>
            <p>
                {t("availableAtReception")}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ibisTunis" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/ibistunis/" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} IBIS Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default LivretClient
